import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { PlatformOrganizationDetail, Program } from '@starter/shared';
import {
  CreatePlatformProgramInputSchema,
  PlatformProgramDirectorInputSchema,
} from '@starter/shared';
import { api, ApiError } from '../../api/client.js';
import { ErrorBanner } from '../../components/ErrorBanner.js';
import { LoadingState } from '../../components/LoadingState.js';
import { AccessibleModal } from '../../components/AccessibleModal.js';
import { PageHeader } from '../../components/PageHeader.js';

const PROVISION_SUCCESS =
  'If this person is eligible, they will receive program director access. No further action is required from you.';

export function PlatformOrganizationDetailPage() {
  const { organizationId } = useParams<{ organizationId: string }>();
  const [org, setOrg] = useState<PlatformOrganizationDetail | null>(null);
  const [programs, setPrograms] = useState<Program[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusBusy, setStatusBusy] = useState(false);
  const [programName, setProgramName] = useState('');
  const [programBusy, setProgramBusy] = useState(false);
  const [pdProgramId, setPdProgramId] = useState<string | null>(null);
  const [pdName, setPdName] = useState('');
  const [pdEmail, setPdEmail] = useState('');
  const [pdMessage, setPdMessage] = useState<string | null>(null);
  const [pdBusy, setPdBusy] = useState(false);
  const [confirmDeactivate, setConfirmDeactivate] = useState(false);
  const [confirmProgramDeactivateId, setConfirmProgramDeactivateId] = useState<string | null>(null);

  const load = useCallback(() => {
    if (!organizationId) return;
    setLoading(true);
    setError(null);
    Promise.all([
      api.platformGetOrganization(organizationId),
      api.platformListOrganizationPrograms(organizationId, { page: 1, pageSize: 50 }),
    ])
      .then(([orgRes, progRes]) => {
        setOrg(orgRes);
        setPrograms(progRes.data);
      })
      .catch((err) => setError(err instanceof ApiError ? err.message : 'Failed to load organization'))
      .finally(() => setLoading(false));
  }, [organizationId]);

  useEffect(load, [load]);

  async function toggleOrgStatus() {
    if (!org || statusBusy) return;
    if (org.status === 'active') {
      if (!confirmDeactivate) {
        setConfirmDeactivate(true);
        return;
      }
    }
    setStatusBusy(true);
    try {
      const next = org.status === 'active' ? 'inactive' : 'active';
      const updated = await api.platformSetOrganizationStatus(org.id, next);
      setOrg({ ...org, ...updated });
      setConfirmDeactivate(false);
      load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Status update failed');
    } finally {
      setStatusBusy(false);
    }
  }

  async function createProgram(e: React.FormEvent) {
    e.preventDefault();
    if (!organizationId) return;
    const parsed = CreatePlatformProgramInputSchema.safeParse({ name: programName });
    if (!parsed.success) return;
    setProgramBusy(true);
    try {
      await api.platformCreateProgram(organizationId, parsed.data);
      setProgramName('');
      load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Program create failed');
    } finally {
      setProgramBusy(false);
    }
  }

  function requestProgramDeactivate(programId: string) {
    setConfirmProgramDeactivateId(programId);
  }

  async function confirmProgramDeactivate() {
    const program = programs.find((p) => p.id === confirmProgramDeactivateId);
    if (!program || program.status !== 'active') return;
    try {
      await api.platformSetProgramStatus(program.id, 'inactive');
      setConfirmProgramDeactivateId(null);
      load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Program status update failed');
    }
  }

  async function activateProgram(program: Program) {
    if (program.status !== 'inactive') return;
    try {
      await api.platformSetProgramStatus(program.id, 'active');
      load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Program status update failed');
    }
  }

  async function provisionPd(e: React.FormEvent) {
    e.preventDefault();
    if (!pdProgramId) return;
    const parsed = PlatformProgramDirectorInputSchema.safeParse({ fullName: pdName, email: pdEmail });
    if (!parsed.success) return;
    setPdBusy(true);
    setPdMessage(null);
    try {
      await api.platformProvisionProgramDirector(pdProgramId, parsed.data);
      setPdMessage(PROVISION_SUCCESS);
      setPdName('');
      setPdEmail('');
      setPdProgramId(null);
      load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Provisioning failed');
    } finally {
      setPdBusy(false);
    }
  }

  if (loading) return <LoadingState message="Loading organization…" />;
  if (error && !org) return <ErrorBanner message={error} onRetry={load} />;
  if (!org) return null;

  const onboardingItems = [
    ['Active program', org.onboarding.hasActiveProgram],
    ['Program director', org.onboarding.hasActiveProgramDirector],
    ['Instructors', org.onboarding.hasInstructors],
    ['Students', org.onboarding.hasStudents],
    ['Requirement plan', org.onboarding.hasRequirementPlan],
    ['Clinical sites', org.onboarding.hasClinicalSites],
    ['Specialties or procedures', org.onboarding.hasSpecialtiesOrProcedures],
  ] as const;

  return (
    <div className="page">
      <PageHeader
        title={org.name}
        subtitle="Organization detail"
        actions={<Link to="/platform/organizations" className="btn btn--ghost">Back to list</Link>}
      />
      {error && <ErrorBanner message={error} onRetry={load} />}

      <section className="card">
        <h2 className="card__title">Status</h2>
        <p>
          <span className={`status-pill status-pill--${org.status}`}>{org.status}</span>
        </p>
        {confirmDeactivate && (
          <div className="banner banner--warning" role="alert">
            Deactivating marks the organization inactive. Programs and data are not deleted. Confirm to continue.
            <div className="modal__actions">
              <button type="button" className="btn btn--ghost" onClick={() => setConfirmDeactivate(false)}>Cancel</button>
              <button type="button" className="btn btn--primary" disabled={statusBusy} onClick={() => void toggleOrgStatus()}>
                Deactivate organization
              </button>
            </div>
          </div>
        )}
        {!confirmDeactivate && (
          <button type="button" className="btn btn--secondary" disabled={statusBusy} onClick={() => void toggleOrgStatus()}>
            {org.status === 'active' ? 'Deactivate organization' : 'Activate organization'}
          </button>
        )}
      </section>

      <section className="card">
        <h2 className="card__title">Onboarding indicators</h2>
        <ul className="onboarding-list">
          {onboardingItems.map(([label, done]) => (
            <li key={label} className={done ? 'onboarding-list__done' : ''}>
              {done ? '✓' : '○'} {label}
            </li>
          ))}
        </ul>
      </section>

      <section className="card">
        <h2 className="card__title">Programs</h2>
        <form className="platform-inline-form" onSubmit={createProgram}>
          <label className="field field--inline">
            <span className="field__label">New program name</span>
            <input value={programName} onChange={(e) => setProgramName(e.target.value)} disabled={programBusy} />
          </label>
          <button type="submit" className="btn btn--primary" disabled={programBusy}>Create program</button>
        </form>
        {confirmProgramDeactivateId && (
          <div className="banner banner--warning" role="alert">
            Deactivating marks the program inactive. Memberships and data are not deleted. Confirm to continue.
            <div className="modal__actions">
              <button type="button" className="btn btn--ghost" onClick={() => setConfirmProgramDeactivateId(null)}>
                Cancel
              </button>
              <button type="button" className="btn btn--primary" onClick={() => void confirmProgramDeactivate()}>
                Deactivate program
              </button>
            </div>
          </div>
        )}
        {programs.length === 0 ? (
          <p className="empty-state">No programs yet.</p>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {programs.map((p) => (
                  <tr key={p.id}>
                    <td>{p.name}</td>
                    <td><span className={`status-pill status-pill--${p.status}`}>{p.status}</span></td>
                    <td className="actions-cell">
                      {p.status === 'active' ? (
                        confirmProgramDeactivateId !== p.id ? (
                          <button
                            type="button"
                            className="btn btn--ghost btn--sm"
                            onClick={() => requestProgramDeactivate(p.id)}
                          >
                            Deactivate
                          </button>
                        ) : null
                      ) : (
                        <button type="button" className="btn btn--ghost btn--sm" onClick={() => void activateProgram(p)}>
                          Activate
                        </button>
                      )}
                      <button
                        type="button"
                        className="btn btn--secondary btn--sm"
                        aria-label={`Provision PD for ${p.name}`}
                        onClick={() => setPdProgramId(p.id)}
                      >
                        Provision PD
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <AccessibleModal
        open={pdProgramId !== null}
        onClose={() => setPdProgramId(null)}
        labelledBy="pd-title"
      >
        <h2 id="pd-title">Provision program director</h2>
        <form onSubmit={provisionPd}>
          <label className="field">
            <span className="field__label">Full name</span>
            <input value={pdName} onChange={(e) => setPdName(e.target.value)} disabled={pdBusy} />
          </label>
          <label className="field">
            <span className="field__label">Email</span>
            <input type="email" value={pdEmail} onChange={(e) => setPdEmail(e.target.value)} disabled={pdBusy} />
          </label>
          <div className="modal__actions">
            <button type="button" className="btn btn--ghost" onClick={() => setPdProgramId(null)}>Cancel</button>
            <button type="submit" className="btn btn--primary" disabled={pdBusy}>
              {pdBusy ? 'Submitting…' : 'Submit'}
            </button>
          </div>
        </form>
      </AccessibleModal>
      {pdMessage && <div className="banner banner--success" role="status">{pdMessage}</div>}
    </div>
  );
}
